import { Button, type HTMLButtonProps, Modal } from '@odin-labs/components';
import { useGetSiteGatewaysQuery } from 'apollo/generated/client-operations';
import { seedHelp } from 'containers/jobsiteConfiguration/tabs/siteLayout/labels';
import React from 'react';
import { refusalText } from 'siteLayout';

const classes = {
  body: 'odin-flex odin-flex-col odin-gap-4',
  lead: 'odin-text-sm odin-text-gray-900',
  gateways: 'odin-list-disc odin-pl-5 odin-text-sm odin-text-gray-900',
};

export type SeedRequiredModalProps = {
  siteId: string;
  /** The gateways a publish was refused for, linked to no entry; null when closed. */
  gatewayIds: string[] | null;
  onCancel: () => void;
  /** Sends the seed, as Add entries from gateways does. */
  onSubmit: () => void;
};

/**
 * The seed's button, telling on hover that a removed entry is not added
 * again. `Modal`'s `actionButton` (@odin-labs/components 2026.7.0) passes
 * `HTMLButtonProps`, whose `ref` type `Button` does not accept; no ref is
 * passed, so it is left out.
 */
function SeedButton(props: Omit<HTMLButtonProps, 'ref'>): React.ReactElement {
  return (
    <span title={seedHelp}>
      <Button {...props} />
    </span>
  );
}

/**
 * Names the gateways a publish was refused for and offers the seed. The
 * seed links a gateway to the draft entry of its name, or adds an entry for
 * it, asking for the zones it cannot choose itself.
 */
export function SeedRequiredModal({
  siteId,
  gatewayIds,
  onCancel,
  onSubmit,
}: SeedRequiredModalProps): React.ReactElement {
  const { data } = useGetSiteGatewaysQuery({
    variables: { siteId },
    skip: !gatewayIds,
    fetchPolicy: 'no-cache',
  });
  const names = new Map(
    (data?.getJobsite?.jobsiteGateways.edges ?? []).map(({ node }) => [
      node.id,
      node.name,
    ]),
  );
  const ids = gatewayIds ?? [];
  return (
    <Modal
      open={gatewayIds !== null}
      setOpen={(open) => !open && onCancel()}
      title="Add entries for these gateways"
      size="md"
      actionText="Add entries"
      actionButton={SeedButton}
      onAction={onSubmit}
      cancelText="Cancel"
      onCancel={onCancel}
    >
      <div className={classes.body}>
        <p className={classes.lead}>
          {refusalText({ code: 'SEED_REQUIRED', gatewayIds: ids })}
        </p>
        <ul className={classes.gateways}>
          {ids.map((id) => (
            <li key={id}>{names.get(id) ?? 'Gateway'}</li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
